import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import type { FC } from 'react';
import { NAVY, RADIUS_ITEM, STATUS_ERROR, STATUS_NEUTRAL, STATUS_WARN } from 'styles/tokens';

/** Muted grey for inactive-but-not-error states (paused/archived). */
const STATUS_GREY = alpha(NAVY, 0.6);

/**
 * Only `approved` / `reinstated` templates can be sent through Meta. Any other status (pending,
 * rejected, paused, disabled, archived…) must be non-selectable in every template picker.
 */
const SELECTABLE_STATUSES = ['approved', 'reinstated'];

export const isTemplateSelectable = (status?: string): boolean =>
  SELECTABLE_STATUSES.includes((status ?? '').toLowerCase());

/** Spanish label shown on the status tag of a non-selectable template. */
export const getTemplateStatusLabel = (status?: string): string => {
  switch ((status ?? '').toLowerCase()) {
    case 'pending':
      return 'Aprobación pendiente';
    case 'rejected':
      return 'Rechazada';
    case 'paused':
      return 'Pausada';
    case 'disabled':
      return 'Deshabilitada';
    case 'archived':
      return 'Archivada';
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'No disponible';
  }
};

const getTagColor = (status?: string): string => {
  switch ((status ?? '').toLowerCase()) {
    case 'pending':
      return STATUS_WARN;
    case 'rejected':
    case 'disabled':
      return STATUS_ERROR;
    case 'paused':
    case 'archived':
      return STATUS_GREY;
    default:
      return STATUS_NEUTRAL;
  }
};

interface TemplateStatusTagProps {
  status?: string;
}

/**
 * Compact status tag rendered next to a non-approved template in selection menus. Follows the design
 * system: no 1px solid border, tonal tint fill, `RADIUS_ITEM`, Jost. Renders nothing for selectable
 * (approved/reinstated) templates.
 */
const TemplateStatusTag: FC<TemplateStatusTagProps> = ({ status }) => {
  if (isTemplateSelectable(status)) {
    return null;
  }
  const color = getTagColor(status);
  return (
    <Box
      component="span"
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '0.02em',
        color,
        backgroundColor: alpha(color, 0.12),
        borderRadius: RADIUS_ITEM,
        px: 0.875,
        py: 0.125,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {getTemplateStatusLabel(status)}
    </Box>
  );
};

export default TemplateStatusTag;
